import axios from "axios";

export const base_url = "https://api.zvoni.org";
export const base_files_url = "https://api.zvoni.org/files/";

let refreshPromise: Promise<boolean> | null = null;
const fileBlobCache = new Map<string, Promise<Blob>>();

export const saveAuthTokens = (data: any) => {
    if (data?.id) localStorage.setItem("user_id", String(data.id));
    if (data?.access_token || data?.token) localStorage.setItem("user_token", data.access_token ?? data.token);
    if (data?.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
}

export const clearAuth = () => {
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_token");
    localStorage.removeItem("refresh_token");
    fileBlobCache.clear();
}

export const getAuthUserId = () => localStorage.getItem("user_id");

export const getAuthorizationHeader = () => {
    const token = localStorage.getItem("user_token");
    return token ? `Bearer ${token}` : "";
}

const getHeaders = (headers: any = {}) => {
    const authHeader = getAuthorizationHeader();
    return {
        ...headers,
        ...(authHeader ? {Authorization: authHeader} : {}),
    }
}

const refreshAccessToken = () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
        clearAuth();
        return Promise.resolve(false);
    }

    if (refreshPromise) return refreshPromise;

    refreshPromise = axios.post(`${base_url}/auth/refresh`, {
        refresh_token: refreshToken
    }).then(res => {
        const data = res?.data?.response;
        if (data?.access_token && data?.refresh_token) {
            saveAuthTokens(data);
            return true;
        }
        clearAuth();
        return false;
    }).catch(() => {
        clearAuth();
        return false;
    }).finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
}

const isAuthError = (errorOrResponse: any) => {
    const response = errorOrResponse?.response ?? errorOrResponse;
    const status = response?.status;
    const error = response?.data?.error;
    return status === 401 || (status === 400 && (
        error === "unauthorized" ||
        error === "no_auth" ||
        error === "invalid_token" ||
        error === "token_expired"
    ));
}

const requestWithFreshToken = (requestFactory: () => Promise<any>) => {
    return requestFactory().then(res => {
        if (!isAuthError(res)) return res;
        return refreshAccessToken().then(refreshed => refreshed ? requestFactory() : res);
    }).catch(err => {
        if (!isAuthError(err)) throw err;
        return refreshAccessToken().then(refreshed => {
            if (!refreshed) throw err;
            return requestFactory();
        });
    });
}

export const getFileUrl = (file: string | null | undefined) => {
    if (!file) return "";

    const value = String(file);
    if (value.startsWith("blob:") || value.startsWith("data:")) return value;

    const filesUrlWithoutTrailingSlash = base_files_url.replace(/\/$/, "");
    if (value.startsWith(filesUrlWithoutTrailingSlash)) {
        const path = value.slice(filesUrlWithoutTrailingSlash.length).replace(/^\/+/, "");
        return `${base_files_url}${path}`;
    }

    if (/^https?:\/\//.test(value)) return value;

    return `${base_files_url}${value.replace(/^\/+/, "")}`;
}

export const fetchAuthorizedFile = (file: string) => {
    const url = getFileUrl(file);
    if (!url) return Promise.reject(new Error("File URL is empty"));

    if (!fileBlobCache.has(url)) {
        fileBlobCache.set(url, requestWithFreshToken(() => axios.get(url, {
            headers: getHeaders(),
            responseType: "blob",
        })).then(res => res?.data).catch(err => {
            fileBlobCache.delete(url);
            throw err;
        }));
    }

    return fileBlobCache.get(url) as Promise<Blob>;
}

export const openAuthorizedFile = (file: string, fileName?: string) => {
    const openedWindow = window.open("", "_blank");

    return fetchAuthorizedFile(file).then(blob => {
        const objectUrl = URL.createObjectURL(blob);

        if (openedWindow) {
            openedWindow.location.href = objectUrl;
        } else {
            const link = document.createElement("a");
            link.href = objectUrl;
            link.target = "_blank";
            if (fileName) link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
        }

        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    }).catch(err => {
        if (openedWindow) openedWindow.close();
        throw err;
    });
}

export const checkAuthOrRefresh = () => {
    const token = localStorage.getItem("user_token");
    if (!token) return Promise.resolve(null);

    const request = () => axios.post(`${base_url}/auth/checkAuth`, {}, {
        headers: getHeaders({"Content-Type": "application/json"})
    });

    return requestWithFreshToken(request).then(res => res?.data);
}

export const conn = (request: string, data: any = {}, config: any = {}, onProgress?: (e: { loaded: number, total: number })=>void)=>new Promise((resolve)=>{
    const body = request === "auth/checkAuth" ? {} : {data};
    requestWithFreshToken(() => {
        const headers: any = {
            ...getHeaders(config.headers),
        }

        return axios.post(`${base_url}/${request}`, body, {
            ...config,
            headers: headers,
            method: 'post',
            onUploadProgress: function(progressEvent:any) {
                if(onProgress) onProgress({loaded: progressEvent.loaded, total: progressEvent.total});
            }
        }).then(res=>{
            if(res && res.data) {
                return res;
            };
        });
    }).then(res => {
        resolve(res?.data);
    }).catch(err => {
        resolve(err?.response?.data ?? {response: false, error: err?.message ?? "Request error"});
    });
});

export const uploadFile = (file: any, type = "file", onProgress?: (e: { loaded: number, total: number })=>void)=>new Promise((resolve)=>{
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("type", type);
    requestWithFreshToken(() => {
        const headers: any = {
            ...getHeaders({
                'Content-Type': 'multipart/form-data',
            }),
        }

        return axios.post(base_url + '/files/upload', formData, {
            headers: headers,
            onUploadProgress: function(progressEvent:any) {
                if(onProgress) onProgress({loaded: progressEvent.loaded, total: progressEvent.total});
            }
        });
    }).then((res:any)=>{
        if(res && res.data) resolve(res.data);
    }).catch(err => {
        resolve(err?.response?.data ?? {response: false, error: err?.message ?? "Upload error"});
    });
});
